const isPlainPrimaryClick = (event) =>
	event.button === 0 &&
	!event.metaKey &&
	!event.ctrlKey &&
	!event.shiftKey &&
	!event.altKey;

const parseResultsRegion = (html) => {
	const template = document.createElement('template');
	template.innerHTML = String(html).trim();
	const region = template.content.firstElementChild;
	return region?.matches('[data-results-region]') ? region : null;
};

export const initArchiveSubjectNavigation = (
	block,
	onResultsChanged = () => {}
) => {
	const restUrl = block.dataset.restUrl || '';
	const archiveType = block.dataset.archiveType || '';
	const filterLinks = Array.from(
		block.querySelectorAll('a[data-filter-button][data-subject]')
	);
	const status = block.querySelector('[data-results-status]');
	let resultsRegion = block.querySelector('[data-results-region]');

	if (
		!restUrl ||
		!archiveType ||
		filterLinks.length === 0 ||
		!resultsRegion
	) {
		return null;
	}

	let controller = null;
	let requestSequence = 0;
	const cache = new Map();
	const initialSubject = block.dataset.activeSubject || '';
	const initialUrl =
		filterLinks.find(
			(link) => (link.dataset.subject || '') === initialSubject
		)?.href || window.location.href;
	cache.set(initialSubject, {
		count: block.querySelectorAll('[data-card]').length,
		html: resultsRegion.outerHTML,
		url: initialUrl,
	});

	const setActiveSubject = (subject) => {
		block.dataset.activeSubject = subject;
		filterLinks.forEach((link) => {
			const isActive = (link.dataset.subject || '') === subject;
			link.classList.toggle('is-active', isActive);
			if (isActive) {
				link.setAttribute('aria-current', 'page');
			} else {
				link.removeAttribute('aria-current');
			}
		});
	};

	const announce = (count) => {
		if (status) {
			status.textContent = `${count} ${count === 1 ? 'result' : 'results'} shown.`;
		}
	};

	const applyResult = (result, subject, shouldPush) => {
		const replacement = parseResultsRegion(result.html);
		if (!replacement) {
			throw new Error(
				'Archive results response did not contain a valid results region.'
			);
		}

		resultsRegion.replaceWith(replacement);
		resultsRegion = replacement;
		setActiveSubject(subject);
		onResultsChanged();
		announce(result.count);

		if (shouldPush) {
			window.history.pushState(
				{ fcArchiveType: archiveType, fcSubject: subject },
				'',
				result.url
			);
		}
	};

	const loadSubject = async (subject, destinationUrl, shouldPush) => {
		controller?.abort();
		controller = new AbortController();
		const sequence = ++requestSequence;
		block.setAttribute('aria-busy', 'true');

		try {
			let result = cache.get(subject);
			if (!result) {
				const endpoint = new URL(restUrl, window.location.origin);
				if (subject) {
					endpoint.searchParams.set('subject', subject);
				}
				if (block.dataset.maxVisibleTags) {
					endpoint.searchParams.set(
						'max_visible_tags',
						block.dataset.maxVisibleTags
					);
				}

				const response = await fetch(endpoint, {
					headers: { Accept: 'application/json' },
					signal: controller.signal,
				});
				if (!response.ok) {
					throw new Error(
						`Archive results request failed with ${response.status}.`
					);
				}

				const payload = await response.json();
				if (
					typeof payload?.results_html !== 'string' ||
					typeof payload?.url !== 'string' ||
					typeof payload?.count !== 'number' ||
					payload?.subject !== subject
				) {
					throw new Error('Archive results response was malformed.');
				}

				result = {
					count: payload.count,
					html: payload.results_html,
					url: payload.url,
				};
				cache.set(subject, result);
			}

			if (sequence !== requestSequence) {
				return;
			}

			applyResult(result, subject, shouldPush);
		} catch (error) {
			if (error?.name === 'AbortError') {
				return;
			}

			if (shouldPush) {
				window.location.assign(destinationUrl);
			} else {
				window.location.reload();
			}
		} finally {
			if (sequence === requestSequence) {
				block.removeAttribute('aria-busy');
			}
		}
	};

	filterLinks.forEach((link) => {
		link.addEventListener('click', (event) => {
			if (
				!isPlainPrimaryClick(event) ||
				link.target === '_blank' ||
				link.origin !== window.location.origin
			) {
				return;
			}

			event.preventDefault();
			const subject = link.dataset.subject || '';
			if (subject === block.dataset.activeSubject) {
				return;
			}

			loadSubject(subject, link.href, true);
		});
	});

	window.history.replaceState(
		{
			...(window.history.state || {}),
			fcArchiveType: archiveType,
			fcSubject: initialSubject,
		},
		'',
		window.location.href
	);

	window.addEventListener('popstate', (event) => {
		if (event.state?.fcArchiveType !== archiveType) {
			return;
		}

		const subject = event.state.fcSubject || '';
		if (subject !== block.dataset.activeSubject) {
			loadSubject(subject, window.location.href, false);
		}
	});

	return { refresh: onResultsChanged };
};
